import { useMutation } from "@apollo/client";
import { Alert, Button, Dialog, DialogContent, DialogTitle, Grid, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";

import type {
  CallScheduleInfoFragment,
  UpdateCallScheduleMutation,
  UpdateCallScheduleMutationVariables,
} from "@/graphql/graphql";
import { UPDATE_CALL_SCHEDULE } from "@/pages/UserDetails/queries";

import SchedulePicker from "./SchedulePicker/SchedulePicker";

type EditScheduleProps = {
  trigger: boolean;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  callSchedule?: CallScheduleInfoFragment;
};

export const EditScheduleModal: React.FC<EditScheduleProps> = ({
  trigger,
  setTrigger,
  callSchedule,
}) => {
  const [showError, setShowError] = useState<boolean>(false);

  const [editCallSchedule, { error: editCallScheduleError }] = useMutation<
    UpdateCallScheduleMutation,
    UpdateCallScheduleMutationVariables
  >(UPDATE_CALL_SCHEDULE, { refetchQueries: ["GetSingleUser"] });

  useEffect(() => {
    if (editCallScheduleError) {
      setShowError(true);
    }
  }, [editCallScheduleError]);

  const onSubmit = async (cron: string) => {
    if (!callSchedule) {
      return;
    }

    try {
      await editCallSchedule({
        variables: {
          id: callSchedule.id,
          cronExpression: cron,
        },
      });
      setTrigger(false);
    } catch (error) {
      setShowError(true);
    }
  };

  if (!trigger) {
    return null;
  }

  return (
    <>
      <Dialog open={trigger} onClose={() => setTrigger(false)}>
        <Grid container justifyContent="space-between" sx={{ padding: "0.5rem" }}>
          <DialogTitle>Edit Schedule</DialogTitle>
          <Button className="button" onClick={() => setTrigger(false)}>
            X
          </Button>
        </Grid>
        <DialogContent>
          <SchedulePicker
            onSubmit={onSubmit}
            layout="modal"
            cronExpression={callSchedule?.cronExpression}
          />
        </DialogContent>
      </Dialog>

      {showError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showError}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            Failed to update call schedule.
          </Alert>
        </Snackbar>
      )}
    </>
  );
};
